import { useState, useRef, useCallback, FC, useEffect } from 'react';
import {
  Box,
  Button,
  Group,
  Stack,
  Text,
  Card,
  ActionIcon,
  Image,
  Modal,
  FileInput,
  Alert,
} from '@mantine/core';
import {
  IconCamera,
  IconPhoto,
  IconTrash,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export interface PhotoFile {
  id?: string;
  file: File;
  preview: string;
  timestamp: Date;
  source: 'camera' | 'upload';
}

interface PhotoInputProps {
  onChange: (photos: PhotoFile[]) => void;
  initialPhotos?: PhotoFile[];
  maxPhotos?: number;
  label?: string;
}

export const PhotoInput: FC<PhotoInputProps> = ({
  onChange,
  initialPhotos = [],
  maxPhotos = 5,
  label = 'Photos',
}) => {
  const { t } = useTranslation(['patient-pages']);

  const [photos, setPhotos] = useState<PhotoFile[]>(initialPhotos);
  const [cameraModalOpened, setCameraModalOpened] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const prevInitialPhotosRef = useRef<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const serializePhotos = (items: PhotoFile[]) =>
      JSON.stringify(
        items.map((photo) => ({
          id: photo.id ?? null,
          preview: photo.preview,
          timestamp:
            photo.timestamp instanceof Date
              ? photo.timestamp.getTime()
              : photo.timestamp,
          source: photo.source,
          fileName: photo.file?.name ?? null,
          fileSize: photo.file?.size ?? null,
        }))
      );

    const currentSerialized = serializePhotos(initialPhotos);
    if (currentSerialized !== prevInitialPhotosRef.current) {
      setPhotos(initialPhotos);
      prevInitialPhotosRef.current = currentSerialized;
    }
  }, [initialPhotos]);

  const updatePhotos = useCallback(
    (newPhotos: PhotoFile[]) => {
      setPhotos(newPhotos);
      onChange(newPhotos);
    },
    [onChange]
  );

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use front camera by default, can be 'environment' for back
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCapturing(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCapturing(false);
    setCameraError(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0);

    // Convert to blob and create file
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }

        const timestamp = new Date();
        const fileName = `photo_${timestamp.getTime()}.jpg`;
        const file = new File([blob], fileName, { type: 'image/jpeg' });

        const newPhoto: PhotoFile = {
          file,
          preview: canvas.toDataURL('image/jpeg', 0.8),
          timestamp,
          source: 'camera',
        };

        const updatedPhotos = [...photos, newPhoto];
        updatePhotos(updatedPhotos);

        // Close camera modal
        stopCamera();
        setCameraModalOpened(false);
      },
      'image/jpeg',
      0.8
    );
  };

  const handleFileUpload = (files: File[] | null) => {
    if (!files) {
      return;
    }

    const newPhotos: PhotoFile[] = [];

    Array.from(files).forEach((file) => {
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        return;
      }

      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;

        const newPhoto: PhotoFile = {
          file,
          preview,
          timestamp: new Date(),
          source: 'upload',
        };

        newPhotos.push(newPhoto);

        // Update photos when all files are processed
        if (newPhotos.length === files.length) {
          const updatedPhotos = [...photos, ...newPhotos].slice(0, maxPhotos);
          updatePhotos(updatedPhotos);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  function removePhoto(index: number) {
    const updated = photos.filter((_, i) => i !== index);
    setPhotos(updated);
    onChange?.(updated);
  }

  const handleCameraModalOpen = () => {
    setCameraModalOpened(true);
    startCamera();
  };

  const handleCameraModalClose = () => {
    stopCamera();
    setCameraModalOpened(false);
  };

  const canAddMore = photos.length < maxPhotos;

  return (
    <Box>
      <Text size="sm" fw={500} mb="xs">
        {label} ({photos.length}/{maxPhotos})
      </Text>

      {/* Action Buttons */}
      <Group mb="md" grow>
        <Button
          leftSection={<IconCamera size={16} />}
          variant="outline"
          onClick={handleCameraModalOpen}
          disabled={!canAddMore}
        >
          {t('examination.images.takePhoto', {
            ns: 'patient-pages',
          })}
        </Button>

        <FileInput
          placeholder={t('examination.images.uploadPhoto', {
            ns: 'patient-pages',
          })}
          accept="image/*"
          multiple
          onChange={handleFileUpload}
          disabled={!canAddMore}
          color="teal"
          styles={{
            input: {
              borderColor: 'teal',
            },
          }}
        />
      </Group>

      {/* Photo Grid */}
      {photos.length > 0 && (
        <Stack gap="xs">
          {photos.map((photo, index) => (
            <Card key={photo.id} withBorder padding="sm">
              <Group>
                <Image
                  src={photo.preview}
                  alt={`${t('examination.images.photo', {
                    ns: 'patient-pages',
                  })} ${photo.id}`}
                  width={80}
                  height={80}
                  fit="cover"
                  radius="sm"
                />

                <Box style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    {photo.file.name}
                  </Text>
                  <Text size="xs" color="dimmed">
                    {photo.source === 'camera' ? '📸 Camera' : '📁 Upload'} •{' '}
                    {photo.timestamp.toLocaleString()}
                  </Text>
                  <Text size="xs" color="dimmed">
                    {(photo.file.size / 1024 / 1024).toFixed(2)} MB
                  </Text>
                </Box>

                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => removePhoto(index)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Card>
          ))}
        </Stack>
      )}

      {/* Camera Modal */}
      <Modal
        opened={cameraModalOpened}
        onClose={handleCameraModalClose}
        title={t('examination.images.takePhoto', {
          ns: 'patient-pages',
        })}
        size="lg"
      >
        <Stack>
          {cameraError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {cameraError}
            </Alert>
          )}

          <Box style={{ position: 'relative' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: 8,
                display: isCapturing ? 'block' : 'none',
              }}
            />

            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>

          <Group align="center">
            {!isCapturing ? (
              <Button
                onClick={startCamera}
                leftSection={<IconCamera size={16} />}
              >
                {t('examination.images.startCamera', {
                  ns: 'patient-pages',
                })}
              </Button>
            ) : (
              <>
                <Button
                  onClick={capturePhoto}
                  leftSection={<IconPhoto size={16} />}
                >
                  {t('examination.images.capturePhoto', {
                    ns: 'patient-pages',
                  })}
                </Button>
                <Button variant="outline" onClick={stopCamera}>
                  {t('examination.images.cancel', {
                    ns: 'patient-pages',
                  })}
                </Button>
              </>
            )}
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};
